import React from 'react';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import Head from 'next/head';
import { mediaBreakpoint, RESPONSIVE, useResponsive } from '@root/src/services/responsiveProvider';
import { EVENT_LIST_ITEM_TYPES } from '@root/src/types';
import getRoute, { buildAsUrl } from '@services/routeList';
import { textH1, textH3, textP } from '@features/ui/styles/textStyles';
import {
  getNextAndPreviousPage,
  selectEventsIDsList,
  selectEventsTotal,
} from '@features/eventList/slices/eventListServerSliceState';
import Illustration, { ILLUSTRATIONS_NAMES } from '@features/ui/components/Illustration';
import Button, { BUTTON_VARIANT, OwnProps } from '@features/ui/components/Button';
import { ICON_NAME } from '@features/ui/components/Icon';
import Header from '@features/eventList/components/EventCard/Header';
import { getInitialValues } from '@features/eventList/components/EventSearchForm';
import { EventCardForEventsList } from '@features/eventList/components/EventCard/EventCardForEventsList';
import TagFilters from '@features/eventList/components/TagFilters';
import { SerieCardForEventsList } from '@features/serieDetail/components/SerieCard/SerieCardForEventsList';

const EventList = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const responsive = useResponsive();
  const eventsIDs = useSelector(selectEventsIDsList());
  const totalEvents = useSelector(selectEventsTotal);
  const { previousPage, nextPage, previousLink, nextLink } = useSelector(getNextAndPreviousPage);

  const isEmpty = totalEvents === 0;
  return (
    <Container>
      <Title>{t('eventListTemplate_title.message', { count: totalEvents })}</Title>
      {responsive === RESPONSIVE.DESKTOP && (
        <StyledTagFilters />
      )}
      {responsive !== RESPONSIVE.DESKTOP
      && (
        <FiltersMobileButton
          onClick={() => router.push(getRoute.openModal(router).eventFilterMobile())}
        >
          {t('eventListTemplate_filterButton.message')}
        </FiltersMobileButton>
      )}
      <StyledHeader />

      {!isEmpty && (
        <>
          {previousPage != null && (
            <NextAndPreviousButton goToPage={previousPage} linkToPage={previousLink} isPrevious />
          )}
          {eventsIDs.map(({ id, type }) => (type === EVENT_LIST_ITEM_TYPES.event
            ? <EventCardForEventsList key={`e-${id}`} eventID={id} />
            : <SerieCardForEventsList key={`s-${id}`} serieID={id} />))}
          {nextPage != null && (
            <NextAndPreviousButton goToPage={nextPage} linkToPage={nextLink} isPrevious={false} />
          )}
        </>
      )}

      {isEmpty && (
        <NoResultsWrapper>
          <Illustration name={ILLUSTRATIONS_NAMES.SEARCH_NO_RESULTS} width={180} height={120} />
          <h3>{t('eventListTemplate_noResultsTitle.message')}</h3>
          <p>{t('eventListTemplate_noResultsMessage.message')}</p>
          <RefreshButton
            variant={BUTTON_VARIANT.secondary}
            leftIcon={ICON_NAME.refresh}
            text={t('eventListTemplate_noResutsReset.message')}
            onClick={() => {
              router.push(getRoute.events().list({}), null, { scroll: false });
            }}
          />
        </NoResultsWrapper>
      )}
    </Container>
  );
};

export default EventList;

const getUrlRoute = (router, page) => {
  const queryAlreadyInUrl = getInitialValues(router.query);
  return getRoute.events().list({
    ...queryAlreadyInUrl,
    page,
  });
};
const NextAndPreviousButton = ({
  isPrevious,
  goToPage,
  linkToPage,
} : { isPrevious: boolean; goToPage: number; linkToPage: number; }) => {
  const router = useRouter();
  const { t } = useTranslation();

  return (
    <>
      <Head>
        <link
          rel={isPrevious ? 'prev' : 'next'}
          href={buildAsUrl(getUrlRoute(router, linkToPage))}
        />
      </Head>
      <ShowMoreWrapper>
        <Button
          leftIcon={ICON_NAME.moreHoriz}
          text={t('eventListTemplate_showMore.message')}
          onClick={() => {
            const url = getUrlRoute(router, goToPage);
            router.push(url, null, { scroll: false });
          }}
        />
      </ShowMoreWrapper>
    </>
  );
};

const Container = styled.div`
  width: 100%;
  > *:not(:first-child) {
    margin-top: 20px;
  }
  padding-top: 50px;
`;

const StyledHeader = styled(Header)`
  ${mediaBreakpoint.mobile} {
    display: none;
  }
`;
const ShowMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const Title = styled.h1`
  ${textH1};
  color: var(--color-dark);
`;

const NoResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  h3 {
    ${textH3};
  }
  p {
    ${textP};
  }
`;

const RefreshButton = styled(Button)<OwnProps>`
  margin-top: 10px;
`;

const FiltersMobileButton = styled.div`
  border: 1px solid var(--color-grey2);
  border-radius: 22px;
  height: 44px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: 10px 20px;
  ${textP};
  color: var(--color-grey4);
`;

const StyledTagFilters = styled(TagFilters)`
  margin-bottom: 40px;
  margin-top: 24px !important;
`;
